/**
 * ExternalClient settings panel.
 *
 * Two sections:
 *   1. Read-only IMAP/SMTP connection settings for mail client configuration.
 *   2. App-specific password management — required when 2FA is enabled, because
 *      external mail clients (Thunderbird, Apple Mail, etc.) cannot supply a
 *      TOTP code and must authenticate with a pre-issued app password instead.
 *
 * Security requirements enforced here:
 *   - Generated password is held only in component state, never in localStorage.
 *   - Password is shown exactly once; cleared when the user dismisses the dialog.
 *   - TOTP code is sent in POST body, never in a URL/query param.
 *   - Clipboard copy uses the same secure-context-safe pattern as TwoFactorAuth.tsx
 *     (navigator.clipboard with execCommand fallback for plain-HTTP origins).
 *   - No console.log of the password or the TOTP input.
 */

import React, { useState, useEffect } from "react";
import { apiGet, apiPost, apiDelete } from "../../api";
import { t, type Locale } from "../../i18n";

// ─── Types matching the API contract in /api/src/routes/appPasswords.ts ─────

interface AppPasswordInfo {
  id: number;
  label: string;
  created_at: string;
  // revoked_at is null for active passwords; only active ones are listed
  revoked_at: string | null;
}

interface ListAppPasswordsResponse {
  app_passwords: AppPasswordInfo[];
}

interface CreateAppPasswordResponse {
  ok: boolean;
  /** Plaintext password — shown to user exactly once, never persisted. */
  password: string;
  label: string;
}

interface MeResponse {
  email: string;
  recoverySetup: boolean;
}

interface TwoFAStatusResponse {
  enabled: boolean;
  backup_codes_remaining: number;
}

// ─── IMAP/SMTP display values ────────────────────────────────────────────────
// These come from VITE_ env vars injected at build time. The backend IMAP_HOST /
// SMTP_HOST are internal/Tailscale addresses and are not user-facing. Operators
// MUST set VITE_IMAP_HOST and VITE_SMTP_HOST to the publicly reachable hostnames
// (e.g. imap.kveja.com, smtp.kveja.com). See open questions in the code review.
const IMAP_HOST = (import.meta.env.VITE_IMAP_HOST as string | undefined) ?? "imap.kveja.com";
const IMAP_PORT = (import.meta.env.VITE_IMAP_PORT as string | undefined) ?? "993";
const SMTP_HOST = (import.meta.env.VITE_SMTP_HOST as string | undefined) ?? "smtp.kveja.com";
const SMTP_PORT = (import.meta.env.VITE_SMTP_PORT as string | undefined) ?? "587";

type PanelState = "list" | "create-form" | "show-new-password";

export function ExternalClient(props: { locale: Locale }) {
  const { locale } = props;

  const [panelState, setPanelState] = useState<PanelState>("list");
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [twoFaEnabled, setTwoFaEnabled] = useState<boolean | null>(null);
  const [appPasswords, setAppPasswords] = useState<AppPasswordInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create form state
  const [createLabel, setCreateLabel] = useState("");
  const [createTotp, setCreateTotp] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // The newly minted password — held only in component state, cleared on dismiss.
  // Never written to localStorage or any persistent store.
  const [newPassword, setNewPassword] = useState<string | null>(null);
  const [newPasswordLabel, setNewPasswordLabel] = useState<string>("");
  const [copied, setCopied] = useState(false);

  // Per-row revoke state keyed by app password id
  const [revoking, setRevoking] = useState<Record<number, boolean>>({});

  useEffect(() => {
    void loadAll();
  }, []);

  async function loadAll() {
    setLoading(true);
    setError(null);

    const [meRes, statusRes, listRes] = await Promise.all([
      apiGet<MeResponse>("/v1/me"),
      apiGet<TwoFAStatusResponse>("/v1/2fa/status"),
      apiGet<ListAppPasswordsResponse>("/v1/app-passwords"),
    ]);

    setLoading(false);

    if (!meRes.ok) {
      setError(meRes.error);
      return;
    }

    setUserEmail(meRes.data.email);
    setTwoFaEnabled(statusRes.ok ? statusRes.data.enabled : false);

    if (listRes.ok) {
      setAppPasswords(listRes.data.app_passwords);
    }
    // List failures are non-fatal — the 2FA-gating message is more important.
    // The user may not have 2FA enabled so the list may be empty/403.
  }

  // ─── Revoke ──────────────────────────────────────────────────────────────

  async function handleRevoke(id: number) {
    setRevoking((prev) => ({ ...prev, [id]: true }));
    const res = await apiDelete<{ ok: boolean }>(`/v1/app-passwords/${id}`);
    setRevoking((prev) => ({ ...prev, [id]: false }));

    if (res.ok) {
      setAppPasswords((prev) => prev.filter((p) => p.id !== id));
    } else {
      setError(res.error);
    }
  }

  // ─── Create ──────────────────────────────────────────────────────────────

  function handleOpenCreate() {
    setCreateLabel("");
    setCreateTotp("");
    setCreateError(null);
    setPanelState("create-form");
  }

  function handleCancelCreate() {
    setCreateLabel("");
    setCreateTotp("");
    setCreateError(null);
    setPanelState("list");
  }

  async function handleCreate() {
    const labelTrimmed = createLabel.trim();
    if (!labelTrimmed) {
      setCreateError(t(locale, "externalClient.error.labelRequired"));
      return;
    }
    if (createTotp.length !== 6) {
      setCreateError(t(locale, "externalClient.error.totpRequired"));
      return;
    }

    setCreating(true);
    setCreateError(null);

    // TOTP code sent in POST body — never in URL/query params.
    const res = await apiPost<CreateAppPasswordResponse>("/v1/app-passwords", {
      label: labelTrimmed,
      code: createTotp,
    });

    setCreating(false);

    if (!res.ok) {
      setCreateError(res.error);
      return;
    }

    // Store the plaintext password in state only — it will be cleared on dismiss.
    // The TOTP input is wiped immediately after a successful creation so it cannot
    // be reused or accidentally leaked if the user screenshots later steps.
    setNewPassword(res.data.password);
    setNewPasswordLabel(res.data.label);
    setCreateLabel("");
    setCreateTotp("");
    setCopied(false);
    setPanelState("show-new-password");

    // Refresh the list while the password is being displayed so it reflects the
    // newly created entry (label + created_at) without blocking the UX.
    apiGet<ListAppPasswordsResponse>("/v1/app-passwords").then((r) => {
      if (r.ok) setAppPasswords(r.data.app_passwords);
    });
  }

  // ─── Clipboard (secure-context-safe, matches TwoFactorAuth.tsx pattern) ──

  async function handleCopyPassword() {
    if (!newPassword) return;
    setError(null);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(newPassword);
      } else {
        // Fallback for insecure origins (e.g. the plain-HTTP dev host) where
        // navigator.clipboard is undefined. execCommand is deprecated but is
        // the only clipboard path available outside a secure context.
        const ta = document.createElement("textarea");
        ta.value = newPassword;
        ta.style.position = "fixed";
        ta.style.top = "0";
        ta.style.left = "0";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (!ok) throw new Error("execCommand copy returned false");
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t(locale, "externalClient.error.copyFailed"));
    }
  }

  function handleDismissNewPassword() {
    // Clear the plaintext from state now that the user has acknowledged it.
    setNewPassword(null);
    setNewPasswordLabel("");
    setCopied(false);
    setPanelState("list");
  }

  // ─── Formatting helpers ──────────────────────────────────────────────────

  function formatDate(iso: string): string {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return iso;
    }
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  const cardStyle: React.CSSProperties = {
    background: "var(--canvas)",
    border: "1px solid var(--line)",
    borderRadius: "8px",
    padding: "20px",
    marginBottom: "16px",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: "11px",
    fontWeight: 600,
    color: "var(--muted)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "2px",
  };

  const valueStyle: React.CSSProperties = {
    fontSize: "13px",
    fontFamily: "monospace",
    color: "var(--text)",
  };

  const rowStyle: React.CSSProperties = {
    marginBottom: "12px",
  };

  const errorBoxStyle: React.CSSProperties = {
    background: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    borderRadius: "6px",
    padding: "12px",
    marginBottom: "16px",
    color: "#ef4444",
    fontSize: "13px",
  };

  if (loading) {
    return (
      <div>
        <h2 style={{ marginTop: 0, marginBottom: "4px" }}>{t(locale, "settings.externalClient.title")}</h2>
        <p style={{ color: "var(--muted)", marginTop: 0 }}>{t(locale, "common.loading")}</p>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ marginTop: 0, marginBottom: "4px" }}>
        {t(locale, "settings.externalClient.title")}
      </h2>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: "24px" }}>
        {t(locale, "externalClient.description")}
      </p>

      {error && <div style={errorBoxStyle}>{error}</div>}

      {/* ── Section 1: IMAP/SMTP server settings ── */}
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, fontSize: "15px", fontWeight: 600, marginBottom: "16px" }}>
          {t(locale, "externalClient.imap.title")}
        </h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px" }}>
          <div style={rowStyle}>
            <div style={labelStyle}>{t(locale, "externalClient.field.server")}</div>
            <div style={valueStyle}>{IMAP_HOST}</div>
          </div>
          <div style={rowStyle}>
            <div style={labelStyle}>{t(locale, "externalClient.field.port")}</div>
            <div style={valueStyle}>{IMAP_PORT}</div>
          </div>
          <div style={rowStyle}>
            <div style={labelStyle}>{t(locale, "externalClient.field.security")}</div>
            <div style={valueStyle}>{t(locale, "externalClient.security.ssl")}</div>
          </div>
          <div style={rowStyle}>
            <div style={labelStyle}>{t(locale, "externalClient.field.username")}</div>
            <div style={valueStyle}>{userEmail ?? "—"}</div>
          </div>
          <div style={rowStyle}>
            <div style={labelStyle}>{t(locale, "externalClient.field.password")}</div>
            <div style={{ ...valueStyle, color: "var(--muted)", fontFamily: "inherit" }}>
              {t(locale, "externalClient.field.passwordHint")}
            </div>
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, fontSize: "15px", fontWeight: 600, marginBottom: "16px" }}>
          {t(locale, "externalClient.smtp.title")}
        </h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px" }}>
          <div style={rowStyle}>
            <div style={labelStyle}>{t(locale, "externalClient.field.server")}</div>
            <div style={valueStyle}>{SMTP_HOST}</div>
          </div>
          <div style={rowStyle}>
            <div style={labelStyle}>{t(locale, "externalClient.field.port")}</div>
            <div style={valueStyle}>{SMTP_PORT}</div>
          </div>
          <div style={rowStyle}>
            <div style={labelStyle}>{t(locale, "externalClient.field.security")}</div>
            <div style={valueStyle}>{t(locale, "externalClient.security.starttls")}</div>
          </div>
          <div style={rowStyle}>
            <div style={labelStyle}>{t(locale, "externalClient.field.username")}</div>
            <div style={valueStyle}>{userEmail ?? "—"}</div>
          </div>
          <div style={rowStyle}>
            <div style={labelStyle}>{t(locale, "externalClient.field.password")}</div>
            <div style={{ ...valueStyle, color: "var(--muted)", fontFamily: "inherit" }}>
              {t(locale, "externalClient.field.passwordHint")}
            </div>
          </div>
        </div>
      </div>

      {/* ── Section 2: App-specific passwords ── */}
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, fontSize: "15px", fontWeight: 600, marginBottom: "8px" }}>
          {t(locale, "externalClient.appPasswords.title")}
        </h3>

        {/* If 2FA is not enabled, explain that it's a prerequisite */}
        {twoFaEnabled === false && (
          <div style={{
            background: "rgba(234, 179, 8, 0.1)",
            border: "1px solid rgba(234, 179, 8, 0.3)",
            borderRadius: "6px",
            padding: "12px",
            marginBottom: "16px",
            color: "var(--text)",
            fontSize: "13px",
            lineHeight: "1.5",
          }}>
            {t(locale, "externalClient.appPasswords.requires2fa")}
          </div>
        )}

        {twoFaEnabled === true && (
          <>
            <p style={{ color: "var(--muted)", fontSize: "13px", marginTop: 0, marginBottom: "16px" }}>
              {t(locale, "externalClient.appPasswords.description")}
            </p>

            {/* ── List state ── */}
            {panelState === "list" && (
              <>
                {appPasswords.length === 0 ? (
                  <p style={{ color: "var(--muted)", fontSize: "13px", marginBottom: "16px" }}>
                    {t(locale, "externalClient.appPasswords.empty")}
                  </p>
                ) : (
                  <table style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginBottom: "16px",
                    fontSize: "13px",
                  }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left", padding: "6px 0", color: "var(--muted)", fontWeight: 600, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid var(--line)" }}>
                          {t(locale, "externalClient.appPasswords.col.label")}
                        </th>
                        <th style={{ textAlign: "left", padding: "6px 0", color: "var(--muted)", fontWeight: 600, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid var(--line)" }}>
                          {t(locale, "externalClient.appPasswords.col.created")}
                        </th>
                        <th style={{ textAlign: "right", padding: "6px 0", borderBottom: "1px solid var(--line)" }} />
                      </tr>
                    </thead>
                    <tbody>
                      {appPasswords.map((ap) => (
                        <tr key={ap.id}>
                          <td style={{ padding: "10px 0", borderBottom: "1px solid var(--line)", color: "var(--text)" }}>
                            {ap.label}
                          </td>
                          <td style={{ padding: "10px 0", borderBottom: "1px solid var(--line)", color: "var(--muted)" }}>
                            {formatDate(ap.created_at)}
                          </td>
                          <td style={{ padding: "10px 0", borderBottom: "1px solid var(--line)", textAlign: "right" }}>
                            <button
                              onClick={() => void handleRevoke(ap.id)}
                              disabled={revoking[ap.id]}
                              style={{
                                background: "transparent",
                                border: "1px solid var(--control-border)",
                                color: "var(--danger)",
                                padding: "4px 10px",
                                borderRadius: "4px",
                                cursor: revoking[ap.id] ? "not-allowed" : "pointer",
                                fontSize: "12px",
                                opacity: revoking[ap.id] ? 0.6 : 1,
                              }}
                            >
                              {revoking[ap.id]
                                ? t(locale, "externalClient.appPasswords.revoking")
                                : t(locale, "externalClient.appPasswords.revoke")}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <button
                  onClick={handleOpenCreate}
                  style={{
                    background: "var(--accent)",
                    color: "white",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  {t(locale, "externalClient.appPasswords.create")}
                </button>
              </>
            )}

            {/* ── Create form state ── */}
            {panelState === "create-form" && (
              <div>
                {createError && <div style={errorBoxStyle}>{createError}</div>}

                <div style={{ marginBottom: "12px" }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "4px" }}>
                    {t(locale, "externalClient.appPasswords.labelField")}
                  </label>
                  <input
                    type="text"
                    value={createLabel}
                    onChange={(e) => setCreateLabel(e.target.value.slice(0, 64))}
                    placeholder={t(locale, "externalClient.appPasswords.labelPlaceholder")}
                    maxLength={64}
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid var(--control-border)",
                      borderRadius: "4px",
                      fontSize: "13px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "4px" }}>
                    {t(locale, "externalClient.appPasswords.totpField")}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={createTotp}
                    onChange={(e) => setCreateTotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    style={{
                      width: "140px",
                      padding: "8px",
                      border: "1px solid var(--control-border)",
                      borderRadius: "4px",
                      fontSize: "14px",
                      fontFamily: "monospace",
                      letterSpacing: "8px",
                      textAlign: "center",
                    }}
                  />
                </div>

                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => void handleCreate()}
                    disabled={creating || createLabel.trim().length === 0 || createTotp.length !== 6}
                    style={{
                      background: "var(--accent)",
                      color: "white",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: "6px",
                      cursor: (creating || createLabel.trim().length === 0 || createTotp.length !== 6) ? "not-allowed" : "pointer",
                      fontSize: "13px",
                      fontWeight: 600,
                      opacity: (creating || createLabel.trim().length === 0 || createTotp.length !== 6) ? 0.6 : 1,
                    }}
                  >
                    {creating
                      ? t(locale, "externalClient.appPasswords.creating")
                      : t(locale, "externalClient.appPasswords.createConfirm")}
                  </button>
                  <button
                    onClick={handleCancelCreate}
                    disabled={creating}
                    style={{
                      background: "transparent",
                      border: "1px solid var(--control-border)",
                      color: "var(--text)",
                      padding: "8px 16px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "13px",
                    }}
                  >
                    {t(locale, "common.cancel")}
                  </button>
                </div>
              </div>
            )}

            {/* ── Show new password state — displayed exactly once ── */}
            {panelState === "show-new-password" && newPassword && (
              <div>
                <div style={{
                  background: "rgba(34, 197, 94, 0.08)",
                  border: "1px solid rgba(34, 197, 94, 0.3)",
                  borderRadius: "6px",
                  padding: "12px",
                  marginBottom: "12px",
                  fontSize: "13px",
                  color: "var(--text)",
                  lineHeight: "1.5",
                }}>
                  {t(locale, "externalClient.appPasswords.showOnceWarning")}
                </div>

                <p style={{ fontSize: "13px", color: "var(--muted)", marginTop: 0, marginBottom: "8px" }}>
                  {t(locale, "externalClient.appPasswords.newPasswordFor", { label: newPasswordLabel })}
                </p>

                <div style={{
                  background: "var(--bg)",
                  border: "1px solid var(--line)",
                  borderRadius: "6px",
                  padding: "12px 16px",
                  marginBottom: "16px",
                  fontFamily: "monospace",
                  fontSize: "14px",
                  letterSpacing: "0.05em",
                  wordBreak: "break-all",
                  color: "var(--text)",
                  userSelect: "all",
                }}>
                  {newPassword}
                </div>

                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => void handleCopyPassword()}
                    style={{
                      background: "transparent",
                      border: "1px solid var(--control-border)",
                      color: copied ? "var(--success)" : "var(--text)",
                      padding: "8px 16px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "13px",
                    }}
                  >
                    {copied
                      ? t(locale, "externalClient.appPasswords.copied")
                      : t(locale, "externalClient.appPasswords.copy")}
                  </button>
                  <button
                    onClick={handleDismissNewPassword}
                    style={{
                      background: "var(--accent)",
                      color: "white",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 600,
                    }}
                  >
                    {t(locale, "externalClient.appPasswords.done")}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
